import {
  parsePackageDisplayUsd,
  resolveHotelAgentHandlingFeeIls,
} from "./resolveHotelAgentOfferDisplayTotal";
import { buildLockedUsdToIlsFn } from "./resolveHotelBookingSummaryPricingSsot";
import { ceilUsd } from "./ceilUsd";

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function readPublicOfferPricingContext(payload) {
  const root = payload && typeof payload === "object" ? payload : {};
  const vm =
    root.public_view_model && typeof root.public_view_model === "object"
      ? root.public_view_model
      : {};
  const pricing = vm.pricing && typeof vm.pricing === "object" ? vm.pricing : {};
  const selection =
    root.selection && typeof root.selection === "object" ? root.selection : {};
  const selectedPackage =
    selection.selected_package && typeof selection.selected_package === "object"
      ? selection.selected_package
      : {};
  const ourPrice = selectedPackage.ourPrice || vm.ourPrice || {};
  const lockedSnap =
    root.pricingSnapshot ||
    selectedPackage.selectedPriceSnapshot?.pricingSnapshot ||
    selectedPackage.pricingSnapshot ||
    pricing.pricingSnapshot ||
    null;
  const lockedUsdToIls = buildLockedUsdToIlsFn(lockedSnap, null);
  const priceIncludesHandlingFee =
    root.price_includes_handling_fee === true ||
    vm.priceIncludesHandlingFee === true;

  return {
    root,
    vm,
    pricing,
    selectedPackage,
    ourPrice,
    lockedSnap,
    lockedUsdToIls,
    priceIncludesHandlingFee,
  };
}

/**
 * SSOT — סה"כ ללקוח בעמוד /offers/public (מלון + דמי טיפול).
 * @param {object|null|undefined} payload stored public-offer payload
 * @returns {number|null}
 */
export function resolvePublicHotelOfferCustomerTotalIls(payload) {
  const {
    root,
    vm,
    pricing,
    selectedPackage,
    ourPrice,
    lockedSnap,
    lockedUsdToIls,
    priceIncludesHandlingFee,
  } = readPublicOfferPricingContext(payload);

  const explicitTotal =
    toNum(vm.totalWithAgentFeeIls) ??
    toNum(pricing.totalWithAgentFeeIls) ??
    toNum(root.total_with_agent_fee_ils) ??
    toNum(root.customer_total_ils);

  if (explicitTotal != null && explicitTotal > 0) {
    return Math.round(explicitTotal);
  }

  if (priceIncludesHandlingFee) {
    const stamped =
      toNum(root.final_price_ils) ??
      toNum(vm.final_price_ils) ??
      toNum(vm.totalPrice) ??
      toNum(vm.price);
    if (stamped != null && stamped > 0) return Math.round(stamped);
  }

  const hotelTotalIls =
    toNum(vm.hotelTotalIls) ??
    toNum(pricing.hotelTotalIls) ??
    toNum(root.hotel_total_ils) ??
    toNum(vm.basePrice) ??
    toNum(vm.base_price) ??
    toNum(root.final_price_ils) ??
    toNum(vm.final_price_ils) ??
    toNum(vm.totalPrice) ??
    toNum(vm.price);

  let agentFeeIls =
    toNum(vm.handlingFee) ??
    toNum(vm.handling_fee) ??
    toNum(pricing.agentFeeIls) ??
    toNum(pricing.handlingFee) ??
    toNum(root.handling_fee_ils);

  if ((agentFeeIls == null || agentFeeIls <= 0) && hotelTotalIls != null) {
    agentFeeIls = resolveHotelAgentHandlingFeeIls(
      ourPrice,
      hotelTotalIls,
      lockedUsdToIls
    );
  }

  if (hotelTotalIls != null) {
    return Math.round(hotelTotalIls + (agentFeeIls || 0));
  }

  return null;
}

/**
 * SSOT — סה"כ ללקוח ב-USD בעמוד /offers/public (מלון + דמי טיפול).
 * @param {object|null|undefined} payload stored public-offer payload
 * @returns {number|null}
 */
export function resolvePublicHotelOfferCustomerTotalUsd(payload) {
  const {
    root,
    vm,
    pricing,
    selectedPackage,
    ourPrice,
    lockedSnap,
    lockedUsdToIls,
    priceIncludesHandlingFee,
  } = readPublicOfferPricingContext(payload);

  const explicitTotal =
    toNum(vm.totalWithAgentFeeUsd) ??
    toNum(pricing.totalWithAgentFeeUsd) ??
    toNum(root.total_with_agent_fee_usd) ??
    toNum(root.customer_total_usd);

  if (explicitTotal != null && explicitTotal > 0) {
    return ceilUsd(explicitTotal);
  }

  if (priceIncludesHandlingFee) {
    const stamped =
      toNum(root.final_price_usd) ??
      toNum(vm.final_price_usd) ??
      toNum(vm.totalPriceUsd);
    if (stamped != null && stamped > 0) return ceilUsd(stamped);
  }

  const hotelTotalUsd =
    toNum(vm.hotelTotalUsd) ??
    toNum(pricing.hotelTotalUsd) ??
    toNum(root.hotel_total_usd) ??
    parsePackageDisplayUsd(selectedPackage.displayCurrencies) ??
    (String(lockedSnap?.supplierCurrency || "").toUpperCase() === "USD"
      ? toNum(lockedSnap?.hotelTotalOriginalCurrency)
      : null);

  let agentFeeUsd =
    toNum(vm.handlingFeeUsd) ??
    toNum(pricing.agentFeeUsd) ??
    toNum(pricing.handlingFeeUsd) ??
    toNum(root.handling_fee_usd);

  const totalIls = resolvePublicHotelOfferCustomerTotalIls(payload);
  const exchangeRate = toNum(lockedSnap?.lockedExchangeRateToIls);

  if ((agentFeeUsd == null || agentFeeUsd <= 0) && totalIls != null && exchangeRate > 0) {
    const hotelTotalIls =
      toNum(vm.hotelTotalIls) ??
      toNum(pricing.hotelTotalIls) ??
      toNum(root.hotel_total_ils);
    let agentFeeIls =
      toNum(vm.handlingFee) ??
      toNum(pricing.agentFeeIls) ??
      toNum(root.handling_fee_ils);
    if ((agentFeeIls == null || agentFeeIls <= 0) && hotelTotalIls != null) {
      agentFeeIls = resolveHotelAgentHandlingFeeIls(
        ourPrice,
        hotelTotalIls,
        lockedUsdToIls
      );
    }
    if (agentFeeIls != null && agentFeeIls > 0) {
      agentFeeUsd = agentFeeIls / exchangeRate;
    }
  }

  if (hotelTotalUsd != null && hotelTotalUsd > 0) {
    return ceilUsd(hotelTotalUsd + (agentFeeUsd || 0));
  }

  if (totalIls != null && totalIls > 0 && exchangeRate > 0) {
    return ceilUsd(totalIls / exchangeRate);
  }

  const vmDisplayUsd = parsePackageDisplayUsd(vm.displayCurrencies);
  if (vmDisplayUsd != null && vmDisplayUsd > 0) {
    return ceilUsd(vmDisplayUsd);
  }

  return null;
}
